// Builds the recorder/camera hierarchy the admin table and the live tree both
// render.
//
// A recorder's channels arrive from the API as ordinary cameras that happen to
// carry a device_id. Left flat, a 32-channel NVR contributes 32 rows with
// near-identical names — "PT - IPD-BBE_C2HU_Patrol View" next to
// "PT - IPD-BBE_FIX_C2HU_Fixed View" — and nothing on screen says which
// physical box to power-cycle when they all go dark at once. Nesting them
// under the recorder is what makes that legible.
//
// This is a pure function so the grouping rules are testable without a DOM:
// they are the kind of thing that looks obviously right and is quietly wrong
// for the camera whose recorder was deleted.

import type { Camera, Device } from "../../lib/api";

/** One line in the rendered tree: either a recorder, or a camera. */
export type DeviceTreeRow =
  | { kind: "device"; id: string; device: Device; cameras: Camera[]; enabledCount: number }
  | { kind: "camera"; id: string; camera: Camera; /** nested under a recorder */ isChild: boolean };

/**
 * Orders recorders (each followed by its cameras, when expanded) ahead of the
 * directly-connected cameras.
 *
 * Recorders come first because they are the larger unit: an operator scanning
 * the page is looking for a box, and the loose cameras are the remainder. Both
 * groups are sorted by name so the order does not shift between renders.
 *
 * A camera whose device_id matches no visible recorder is deliberately treated
 * as standalone rather than dropped. That happens for real — a viewer whose
 * scope grants individual cameras but not the recorder's project — and a
 * camera silently missing from the list is far worse than one shown without
 * its parent.
 */
export function buildDeviceTree(
  devices: Device[],
  cameras: Camera[],
  expandedDeviceIds: ReadonlySet<string>,
): DeviceTreeRow[] {
  const byDevice = new Map<string, Camera[]>();
  const standalone: Camera[] = [];

  const knownDeviceIds = new Set(devices.map((d) => d.id));

  for (const camera of cameras) {
    const parent = camera.device_id;
    if (parent && knownDeviceIds.has(parent)) {
      const siblings = byDevice.get(parent);
      if (siblings) {
        siblings.push(camera);
      } else {
        byDevice.set(parent, [camera]);
      }
    } else {
      standalone.push(camera);
    }
  }

  // Channel order, not name order, inside a recorder: an operator reading the
  // list alongside the recorder's own screens is matching channel numbers.
  for (const siblings of byDevice.values()) {
    siblings.sort((a, b) => (a.channel_id ?? 0) - (b.channel_id ?? 0));
  }

  const rows: DeviceTreeRow[] = [];

  for (const device of [...devices].sort(byName)) {
    const children = byDevice.get(device.id) ?? [];
    rows.push({
      kind: "device",
      id: device.id,
      device,
      cameras: children,
      enabledCount: children.filter((c) => c.enabled).length,
    });

    if (expandedDeviceIds.has(device.id)) {
      for (const camera of children) {
        rows.push({ kind: "camera", id: camera.id, camera, isChild: true });
      }
    }
  }

  for (const camera of standalone.sort(byName)) {
    rows.push({ kind: "camera", id: camera.id, camera, isChild: false });
  }

  return rows;
}

function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name);
}
